
/* ==================================================
   File: src/components/employees/EmployeeDetailsDrawer/hooks/useEmployeeForm.ts
   Hook for form state and CRUD handlers (uses your EmployeeOperation in service layer)
   ================================================== */
import { useContext, useEffect, useState } from "react";
import { Form, message } from "antd";
import { buildInitialValues } from "../helpers/formInitialValues";
import { getManagerDetails } from "../helpers/managerHelpers";
import { EmployeeDetails } from "../../../serviceoperation/entity/EmployeeOperationEntity";
import { EmployeeOperation } from "../../../serviceoperation/EmployeeOperation";
import { CompanyContext } from "../../../contexts/company-context";

interface UseEmployeeFormArgs {
  employee: EmployeeDetails | null;
  onClose: () => void;
  onUpdate: (employee: EmployeeDetails) => void;
  onDelete: (employeeId: string) => void;
  onResendInvite?: (employeeId: string, email: string) => Promise<void>;
}

export const useEmployeeForm = ({ employee, onClose, onUpdate, onDelete, onResendInvite }: UseEmployeeFormArgs) => {
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [resendingInvite, setResendingInvite] = useState<boolean>(false);
  const { selectedCompany } = useContext(CompanyContext);

  useEffect(() => {
    if (employee) {
      const initial = buildInitialValues(employee);
      form.setFieldsValue(initial);
      setIsEditing(false);
    }
  }, [employee, form]);

  const handleSave = async (): Promise<void> => {
    try {
      setIsSaving(true);
      const values = await form.validateFields();

      const manager = getManagerDetails(values.managerId as string | undefined);
      const employeeOps = new EmployeeOperation();
      employeeOps
      .patchEmployeeAsync(
        selectedCompany?.corporationId as number,
        employee!.employeeId,
        {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          phone: values.phone,
          managerId: values.managerId,
          isSyncEnabled: values.isSyncEnabled,
          employeeAttribute: {
            departmentId: values.departmentId,
            roleId: values.role,
          },
        }
      )
      .then((value) => {
        if (value.statusCode === 200) {
          const updatedEmployee: EmployeeDetails = {
            ...(employee as EmployeeDetails),
            firstName: values.firstName as string,
            lastName: values.lastName as string,
            email: values.email as string,
            phone: values.phone as string,
            managerId: values.managerId,
            isSyncEnabled: values.isSyncEnabled as boolean,
            managerName: manager?.name, // Note, this is dummy name for now. I don't really need this object.
          };

          onUpdate(updatedEmployee);
          message.success("Employee information updated successfully!");
          setIsEditing(false);
        } else {
          message.error("Failed to update employee information.");
        }
      })
      .catch(() => {
        message.error("Failed to update employee information.");
      });
    } catch (err) {
      message.error("Please check all required fields");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = (): void => {
    if (!employee) return;
    onDelete(employee.employeeId);
    onClose();
    message.success("Employee deleted successfully");
  };

  const handleCancel = (): void => {
    if (isEditing && employee) {
      form.setFieldsValue(buildInitialValues(employee));
      setIsEditing(false);
      return;
    }
    onClose();
  };

  const handleResendInvite = async (): Promise<void> => {
    if (!employee || !employee.email) {
      message.error("Employee email is required to resend invitation");
      return;
    }

    try {
      setResendingInvite(true);
      const empoyeeOps = new EmployeeOperation();
      
      await empoyeeOps
      .reInviteEmployeeAsync(
        selectedCompany?.corporationId as number,
        employee.employeeId as string
      )
      .then((x) => {
        if (x.statusCode != undefined && x.statusCode === 200) {
          message.success(`Invitation resent to ${employee.email}`);
        } else {
          message.success(
            "An error occured while trying to sent the invitation. Please try again!"
          );
        }
      });
    } catch (err) {
      message.error("An error occured while trying to sent the invitation. Please try again!");
    } finally {
      setResendingInvite(false);
    }
  };

  return {
    form,
    isEditing,
    isSaving,
    resendingInvite,
    setIsEditing,
    handleSave,
    handleCancel,
    handleDelete,
    handleResendInvite,
    initialValues: buildInitialValues(employee!),
  };
};